import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import './styles/GenericBottomSheet.css';

const SWIPE_DOWN_DISMISS_DISTANCE = 80;

export default function GenericBottomSheet({ open=false, close, children, className="", backgroundClassName="", dismissOnSwipeDown=false, ...events }) {
  const didIRemoveTheScroll = useRef(false);
  const touchStart = useRef(null);

  function handleTouchStart(event) {
    touchStart.current = null;
    if (!dismissOnSwipeDown || event.touches.length !== 1 || !event.currentTarget.contains(event.target)) return;

    // A downward gesture should scroll content back to the top before it can dismiss the sheet.
    for (let element = event.target; element && element !== event.currentTarget; element = element.parentElement) {
      if (element.scrollTop > 0) return;
    }

    touchStart.current = {
      x: event.touches[0].clientX,
      y: event.touches[0].clientY,
    };
  }

  function handleTouchEnd(event) {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start || event.changedTouches.length !== 1) return;

    const deltaX = event.changedTouches[0].clientX - start.x;
    const deltaY = event.changedTouches[0].clientY - start.y;
    if (deltaY >= SWIPE_DOWN_DISMISS_DISTANCE && deltaY > Math.abs(deltaX)) {
      event.preventDefault();
      close();
    }
  }

  useEffect(()=>{
    if(open === true) {
      if(!document.body.classList.contains("no-scroll")) {
        document.body.classList.add("no-scroll");
        didIRemoveTheScroll.current = true;
      }
    } else { 
      if(didIRemoveTheScroll.current) {
        document.body.classList.remove("no-scroll");
        didIRemoveTheScroll.current = false;
      }
    }
  }, [open]);

  useEffect(()=>{
    return () => {
      if(didIRemoveTheScroll.current) document.body.classList.remove("no-scroll"); /** Prevents closed popups from removing this class when unmount */
    };
  }, []);

  if(!open) {
    return <></>;
  }

  return createPortal(
    <>
      <div className={`generic-bottom-sheet__background ${backgroundClassName}`} onClick={(e) => {e.stopPropagation(); close();}} {...events}> {/** Although we are using portals, React mantains the same original tree for events. That's why we want to stopPropagating the clickEvent. So that it behaves as if the portal was truly real. In case we need to mantain the original event call stack, we should double check this */}
        <div
          className={`generic-bottom-sheet__card ${className}`}
          onClick={(e) => e.stopPropagation()}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={() => { touchStart.current = null; }}
        >
          {children}
        </div>
      </div>
    </>, document.getElementById("portal-generic-popup")
  );
}
